import { useEffect } from "react";

declare global {
  interface Window {
    Raychat: {
      setUser: (user: RaychatUser) => void;
      getUser: () => RaychatUser | null;
      loadUser: (userId: string) => void;
      unloadUser: () => void;
      setStyle: (options: RaychatStyleOptions) => void;
      setPosition: (options: RaychatPositionOptions) => void;
      openWidget: () => void;
      closeWidget: () => void;
      toggle: () => void;
      hideWidget: () => void;
      showWidget: () => void;
      animate: (animation: string) => void;
      isWidgetOpen: () => boolean;
    };
    RAYCHAT_TOKEN?: string;
    RAYCHAT_HIDE_WIDGET_ON_INIT?: boolean;
  }
}


interface RaychatStyleOptions {
  backgroundColor?: string;
  borderRadius?: string;
  width?: string;
  height?: string;
  boxShadow?: string;
}

interface RaychatPositionOptions {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

interface RaychatUser {
  name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  about?: string;
  updateOnce?: boolean;
  id?: string;
  userId?: string;
}

interface RaychatConfig {
  mobile: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  desktop: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
}

const defaultConfig: RaychatConfig = {
  mobile: {
    bottom: "120px",
    right: "50px",
    left: "auto",
    top: "auto",
  },
  desktop: {
    bottom: "36px",
    right: "110px",
    left: "auto",
    top: "auto",
  },
};

const convertMarginToPosition = (
  marginRight: number,
  marginBottom: number,
  marginLeft: number
): RaychatPositionOptions => {
  return {
    bottom: `${marginBottom}px`,
    right: marginRight > 0 ? `${marginRight}px` : "auto",
    left: marginLeft > 0 ? `${marginLeft}px` : "auto",
    top: "auto",
  };
};

export const useRaychatChat = (
  token: string,
  customConfig?: {
    mobile?: {
      marginRight?: number;
      marginBottom?: number;
      marginLeft?: number;
    };
    desktop?: {
      marginRight?: number;
      marginBottom?: number;
      marginLeft?: number;
    };
  }
) => {
  useEffect(() => {
    window.RAYCHAT_TOKEN = token;

    const config: RaychatConfig = {
      mobile: {
        ...defaultConfig.mobile,
        ...(customConfig?.mobile
          ? convertMarginToPosition(
              customConfig.mobile.marginRight || 50,
              customConfig.mobile.marginBottom || 120,
              customConfig.mobile.marginLeft || 10
            )
          : {}),
      },
      desktop: {
        ...defaultConfig.desktop,
        ...(customConfig?.desktop
          ? convertMarginToPosition(
              customConfig.desktop.marginRight || 110,
              customConfig.desktop.marginBottom || 36,
              customConfig.desktop.marginLeft || 30
            )
          : {}),
      },
    };

    const initRaychat = () => {
      if (document.querySelector('script[src*="widget-react.raychat.io"]')) {
        return;
      }

      window.RAYCHAT_HIDE_WIDGET_ON_INIT = true;

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = "https://widget-react.raychat.io/install/widget.js";
      document.head.appendChild(script);
    };

    const setRaychatWidget = () => {
      if (typeof window.Raychat !== "undefined") {
        const settings =
          window.innerWidth <= 768 ? config.mobile : config.desktop;
        window.Raychat.setPosition(settings);
        window.Raychat.hideWidget();
      }
    };

    initRaychat();

    window.addEventListener("raychat_ready", setRaychatWidget);

    window.addEventListener("resize", setRaychatWidget);

    return () => {
      window.removeEventListener("raychat_ready", setRaychatWidget);
      window.removeEventListener("resize", setRaychatWidget);
    };
  }, [token, customConfig]);
};

const defaultUser: RaychatUser = {
  name: "Guest",
  email: "guest@example.com",
  phone: "0000000000",
};

export const useRaychatUser = (user?: Partial<RaychatUser>) => {
  const setUserRaychat = (userData: RaychatUser) => {
    if (
      typeof window.Raychat !== "undefined" &&
      typeof window.Raychat.setUser === "function"
    ) {
      console.log(userData);
      window.Raychat.setUser(userData);
    } else {
      console.error("Raychat is not initialized or setUser is not available.");
    }
  };

  const getUserRaychat = (): RaychatUser | null => {
    if (
      typeof window.Raychat !== "undefined" &&
      typeof window.Raychat.getUser === "function"
    ) {
      return window.Raychat.getUser() ?? null;
    } else {
      console.error("Raychat is not initialized or getUser is not available.");
      return null;
    }
  };

  const loadUserRaychat = (userId: string) => {
    if (
      typeof window.Raychat !== "undefined" &&
      typeof window.Raychat.loadUser === "function"
    ) {
      console.log("🔹 Loading Raychat User ID:", userId);
      window.Raychat.loadUser(userId);
    } else {
      console.error("❌ [Raychat] loadUser is not available.");
    }
  };

  const unloadUserRaychat = () => {
    if (
      typeof window.Raychat !== "undefined" &&
      typeof window.Raychat.unloadUser === "function"
    ) {
      window.Raychat.unloadUser();
    } else {
      console.error("❌ [Raychat] unloadUser is not available.");
    }
  };

  useEffect(() => {
    if (user && user.name && user.phone) {
      setUserRaychat({ ...defaultUser, ...user });
    }
  }, [user]);

  return { setUserRaychat, getUserRaychat, loadUserRaychat, unloadUserRaychat };
};

